import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import config from "../config";
import BackButton from "./BackButton";
import LoadingSpinner from "./LoadingSpinner";

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    is_verified: false,
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [isResetting, setIsResetting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [messageType, setMessageType] = useState("");
  const [isFormComplete, setIsFormComplete] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrorMessage("");
  };

  useEffect(() => {
    const requiredFields = ["email", "password"];
    const allFieldsFilled = requiredFields.every(
      (field) => formData[field] && formData[field].trim() !== ""
    );

    setIsFormComplete(allFieldsFilled);
  }, [formData]);

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    if (isResetting) return;

    setIsResetting(true);
    try {
      await axios.post(`${config.API_BASE_URL}/auth/forgot_password`, {
        email: forgotPasswordEmail,
      });
      setMessageType("success");
      setErrorMessage("Password reset link sent to your email.");
      setShowForgotPassword(false);
    } catch (error) {
      if (error.response) {
        if (error.response.status === 404) {
          setMessageType("info");
          setErrorMessage(
            "Please check your email, if this account exist, you will receive an password reset email"
          );
        } else {
          setErrorMessage("An error occurred. Please try again.");
        }
      }
    } finally {
      setIsResetting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setIsLoading(true);

    try {
      // 1. First perform login
      const loginResponse = await axios.post(
        `${config.API_BASE_URL}/auth/login`,
        formData
      );

      // Store basic auth data
      sessionStorage.setItem("user_id", loginResponse.data.user_id);
      localStorage.setItem("RestaurantToken", loginResponse.data?.token);

      if (loginResponse.data?.resturant) {
        sessionStorage.setItem(
          "restaurant_id",
          loginResponse.data.resturant.restaurant_id
        );
      }

      // 2. Check subscription status
      const statusResponse = await axios.get(
        `${config.API_BASE_URL}/subscription/subscription-status/${loginResponse.data.user_id}`,
        {
          headers: {
            Authorization: `Bearer ${loginResponse.data.token}`,
          },
        }
      );

      // 3. Handle redirection based on subscription status
      if (
        statusResponse.data.status === "none" ||
        (statusResponse.data.status === "expired" &&
          !statusResponse.data.is_in_grace_period)
      ) {
        navigate("/select-subscription");
      } else {
        sessionStorage.setItem(
          "subscription_id",
          statusResponse.data.subscription_id
        );
        navigate("/dashboard");
      }
    } catch (error) {
      // Enhanced error handling
      if (error.response) {
        switch (error.response.status) {
          case 400:
            setErrorMessage("Invalid request format");
            break;
          case 401:
            setMessageType("error");
            setErrorMessage("Invalid email or password");
            break;
          case 404:
            setErrorMessage("User not found");
            break;
          case 409:
            setErrorMessage("Please verify your email first");
            break;
          case 500:
            setErrorMessage("Server error. Please try again later");
            break;
          default:
            setErrorMessage("An unexpected error occurred");
        }
      } else if (error.request) {
        setErrorMessage("Network error. Please check your connection");
      } else {
        setErrorMessage("An error occurred. Please try again");
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-md mx-auto">
        <BackButton />
        
        {/* Header Section */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl shadow-xl p-8 mb-8 text-center text-white">
          <h2 className="text-3xl font-bold mb-3">Welcome Back</h2>
          <p className="text-blue-100">
            Sign in to your restaurant account
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          {errorMessage && (
            <div
              className={`mb-6 p-4 rounded-xl border-2 ${
                messageType === "success"
                  ? "bg-green-50 border-green-200"
                  : messageType === "info"
                  ? "bg-blue-50 border-blue-200"
                  : "bg-red-50 border-red-200"
              }`}
            >
              <p
                className={`text-center font-semibold ${
                  messageType === "success"
                    ? "text-green-700"
                    : messageType === "info"
                    ? "text-blue-700"
                    : "text-red-700"
                }`}
              >
                {errorMessage}
              </p>
            </div>
          )}

          {!showForgotPassword ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-5">
                <div className="space-y-2">
                  <label
                    htmlFor="email"
                    className="block text-sm font-semibold text-gray-700"
                  >
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
                    placeholder="Enter your email"
                  />
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="password"
                    className="block text-sm font-semibold text-gray-700"
                  >
                    Password
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
                    placeholder="Enter your password"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading || !isFormComplete}
                className={`w-full py-4 rounded-xl font-bold text-lg transition-all duration-300 ${
                  isLoading
                    ? "bg-gray-400 cursor-not-allowed"
                    : isFormComplete
                    ? "bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                    Logging in...
                  </div>
                ) : (
                  "Login"
                )}
              </button>

              <div className="text-center pt-4">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-blue-600 hover:text-blue-800 font-semibold transition-colors duration-200"
                >
                  Forgot your password?
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-2xl font-bold text-gray-800 mb-2">
                  Reset Password
                </h3>
                <p className="text-gray-600">
                  Enter your email to receive a reset link
                </p>
              </div>
              
              <form onSubmit={handleForgotPasswordSubmit} className="space-y-6">
                <div className="space-y-2">
                  <label
                    htmlFor="resetEmail"
                    className="block text-sm font-semibold text-gray-700"
                  >
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    required
                    className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
                    placeholder="Enter your email"
                  />
                </div>

                <div className="flex gap-4">
                  <button
                    type="submit"
                    disabled={isResetting || !forgotPasswordEmail.trim()}
                    className={`flex-1 py-4 rounded-xl font-bold text-lg transition-all duration-300 ${
                      isResetting
                        ? "bg-gray-400 cursor-not-allowed"
                        : forgotPasswordEmail.trim()
                        ? "bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105"
                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                    }`}
                  >
                    {isResetting ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                        Sending...
                      </div>
                    ) : (
                      "Send Reset Link"
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(false)}
                    className="flex-1 py-4 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-xl font-bold hover:from-gray-600 hover:to-gray-700 transition-all duration-300 shadow-lg hover:shadow-xl"
                  >
                    Back to Login
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        {/* Additional Info */}
        <div className="text-center mt-6">
          <p className="text-gray-600">
            Don't have an account?{" "}
            <button
              onClick={() => navigate("/register")}
              className="text-blue-600 hover:text-blue-800 font-semibold transition-colors duration-200"
            >
              Sign up here
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;